import { create } from 'zustand'
import { isApp } from '../utils/platform'

interface Location {
  lat: number
  lng: number
}

/**
 * 마지막으로 받아온 위치를 기기에 남겨둔다.
 *
 * 왜 필요한가
 * ----------
 * 홈 피드는 위치를 순위에 쓰는데(RecommendedFeedService), 정작 홈은 위치를
 * 가져오지 않는다. 지도·검색·랭킹을 다녀와야 스토어에 값이 생긴다. 그래서
 * 추천 피드 요청의 41%가 위치 없이 나갔다.
 *
 * 스토어는 메모리라 앱을 껐다 켜면 사라진다. 여기 남겨두면 다음 실행의
 * 첫 요청부터 위치가 실린다.
 *
 * 24시간까지만 쓴다. 실측으로 같은 사용자가 24시간 뒤에도 90%가 90m 안에
 * 머물렀다(요청 로그 5,872건). 그 이상은 여행 등으로 틀릴 위험이 커진다.
 *
 * granted 는 '전에 위치를 받아낸 적이 있다' = '권한이 있다'는 표시다.
 * 앱 브리지에는 팝업 없이 권한만 확인하는 방법이 없어서(getCurrentLocation 은
 * 권한이 없으면 팝업을 띄운다), 이 기록으로 대신 판단한다.
 */
const STORE_KEY = 'last_location'
const MAX_AGE_MS = 24 * 60 * 60 * 1000

type StoredLocation = { lat: number; lng: number; savedAt: number }

function readStored(): StoredLocation | null {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as StoredLocation
    if (typeof v?.lat !== 'number' || typeof v?.lng !== 'number') return null
    if (!v.savedAt || Date.now() - v.savedAt > MAX_AGE_MS) return null
    return v
  } catch {
    return null
  }
}

function writeStored(location: Location): void {
  try {
    localStorage.setItem(
      STORE_KEY,
      JSON.stringify({ ...location, savedAt: Date.now() })
    )
  } catch {
    // 저장소를 못 쓰는 환경이어도 화면이 멈추면 안 된다
  }
}

/*
 * '전에 위치를 받은 적이 있으면 권한이 있다'로 판단해 홈에서 조용히 새 위치를
 * 받아오려 했는데, 그 전제가 틀려서 뺐다.
 *
 * iOS의 '한 번만 허용'은 그 실행 동안만 유효하고 앱을 껐다 켜면 권한이
 * notDetermined 로 되돌아간다. 그런데 localStorage 에 남긴 위치는 그대로다.
 * 그 상태로 getCurrentLocation 을 부르면 geolocator 가 denied 로 보고
 * requestPermission 을 불러 시스템 팝업이 뜬다. 실제로 홈에서 그렇게 떴다.
 *
 * 팝업 없이 권한만 확인하려면 브리지에 그런 메서드가 있어야 하는데 없다.
 * 그래서 홈은 저장된 위치를 쓰기만 하고, 새로 받는 건 지도·검색·랭킹처럼
 * 사용자가 위치를 기대하는 화면에 맡긴다.
 */

interface LocationStore {
  location: Location | null
  isLoading: boolean
  hasPermission: boolean
  getUserLocation: () => Promise<void>
  requestLocationPermission: () => Promise<boolean>
  setLocation: (location: Location | null) => void
}

export const useLocationStore = create<LocationStore>((set, get) => ({
  // 저장해둔 값이 있으면 그걸로 시작한다. 새 위치를 받으면 곧 덮어쓴다.
  location: readStored(),
  isLoading: false,
  hasPermission: false,

  setLocation: (location) => {
    set({ location })
  },

  requestLocationPermission: async () => {
    // 앱 환경에서는 Flutter 브리지를 통해 권한 요청.
    //
    // 객체 존재만 확인하면 안 된다. 브리지는 핸들러별로 onPageFinished에서
    // 차례로 주입되는데, 먼저 주입되는 핸들러가 window.FlutterBridge를 만들어
    // 두기 때문에 "객체는 있는데 위치 메서드는 아직 없는" 순간이 존재한다.
    // 그때 호출하면 TypeError가 나고 Promise가 reject되어 버튼이 먹통이 된다.
    // 메서드가 없으면 아래 웹 경로(navigator.geolocation)로 넘어간다.
    const bridge = (window as any).FlutterBridge
    if (
      isApp() &&
      bridge &&
      typeof bridge.requestLocationPermission === 'function'
    ) {
      const flutterBridge = bridge

      return new Promise<boolean>((resolve) => {
        let settled = false
        const finish = (granted: boolean) => {
          if (settled) return
          settled = true
          resolve(granted)
        }
        // 위치 권한 결과 콜백 등록
        flutterBridge.onLocationPermissionResult = (data: any) => {
          const granted = !!(data && data.granted)
          set({ hasPermission: granted })
          finish(granted)
        }

        // 위치 권한 요청
        flutterBridge.requestLocationPermission()

        // 네이티브가 응답 안 하면 8초 후 실패 처리(무한 대기 방지)
        setTimeout(() => finish(false), 8000)
      })
    }

    // 웹 환경에서는 권한 요청 없이 바로 위치 정보 요청 시도
    set({ hasPermission: true })

    return true
  },

  getUserLocation: async () => {
    // (예전엔 isLoading이면 조용히 return 했으나, 그 사이 버튼을 누르면 위치 요청이
    //  무시돼 "눌러도 안 움직임"이 됐다. 항상 새로 요청하고 기다린다 — 타임아웃으로 안전.)
    set({ isLoading: true })

    try {
      // 앱 환경에서는 Flutter 브리지 사용
      // 위와 같은 이유로 메서드 존재까지 확인한다(브리지 주입 순서 경쟁).
      const bridge = (window as any).FlutterBridge
      if (
        isApp() &&
        bridge &&
        typeof bridge.getCurrentLocation === 'function'
      ) {
        const flutterBridge = bridge

        return new Promise<void>((resolve) => {
          let settled = false
          const finish = () => {
            if (settled) return
            settled = true
            resolve()
          }
          // 위치 정보 결과 콜백 등록
          flutterBridge.onLocationData = (data: any) => {
            if (data && data.location && !data.error) {
              const location = {
                lat: data.location.latitude,
                lng: data.location.longitude,
              }
              writeStored(location)
              set({ location, isLoading: false, hasPermission: true })
            } else {
              // 위치 서비스가 비활성화된 경우는 조용히 처리 (에러 메시지 표시 안 함)
              const error = data?.error || ''
              if (!error.includes('비활성화')) {
                console.error(
                  '위치 정보를 가져올 수 없습니다:',
                  error || '(메시지 없음)'
                )
              }
              set({ isLoading: false })
            }
            finish()
          }

          // 위치 정보 요청
          flutterBridge.getCurrentLocation()

          // 네이티브가 응답 안 하면 8초 후 로딩 해제(isLoading 고착 방지 → 버튼 재시도 가능)
          setTimeout(() => {
            set({ isLoading: false })
            finish()
          }, 8000)
        })
      } else {
        // 웹 환경에서는 navigator.geolocation 사용
        if (navigator.geolocation) {
          return new Promise<void>((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const location = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                }
                writeStored(location)
                set({ location, isLoading: false, hasPermission: true })
                resolve()
              },
              (error) => {
                console.error('위치 정보를 가져올 수 없습니다:', error)
                set({ isLoading: false, hasPermission: false })
                resolve()
              }
            )
          })
        } else {
          console.error('위치 정보를 사용할 수 없습니다: Geolocation API 없음')
          set({ isLoading: false, hasPermission: false })
        }
      }
    } catch (error) {
      console.error('위치 정보 가져오기 실패:', error)
      set({ isLoading: false })
    }
  },
}))
